/* Created: "Controleren & versturen" dialog for a stored invoice/quote. Shows the exact mail and PDF the server
   will send (same bytes as the attachment), offers that PDF as download, and sends with the previewed version
   fingerprint so a document changed after this preview is never sent unseen. Each opening is one deliberate
   send action; a retry inside the dialog reuses the same action id (no duplicate mails). Paid invoices are
   shown and sent as paid with the same number. */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CircleCheck, Download, Loader as Loader2, RefreshCw, Send, TriangleAlert as AlertTriangle } from 'lucide-react';
import {
  describeQueueResult, pdfBase64ToBlob, previewDocument, sendDocumentMail, type DocumentPreview,
} from '@/lib/transactional-mail';

interface DocumentSendDialogProps {
  documentId: string | null;
  resend?: boolean;
  onClose: () => void;
  onSent?: () => void;
}

export function downloadPreviewPdf(preview: DocumentPreview) {
  const url = URL.createObjectURL(pdfBase64ToBlob(preview.attachment.pdf_base64));
  const a = document.createElement('a');
  a.href = url;
  a.download = preview.attachment.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function DocumentSendDialog({ documentId, resend, onClose, onSent }: DocumentSendDialogProps) {
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [actionId, setActionId] = useState('');

  const load = async (id: string) => {
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      setPreview(await previewDocument(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Voorbeeld laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!documentId) return;
    setActionId(crypto.randomUUID());
    load(documentId);
  }, [documentId]);

  const pdfUrl = useMemo(
    () => (preview ? URL.createObjectURL(pdfBase64ToBlob(preview.attachment.pdf_base64)) : ''),
    [preview],
  );
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const handleSend = async () => {
    if (!documentId || !preview) return;
    setSending(true);
    try {
      const result = await sendDocumentMail(documentId, actionId, preview.fingerprint);
      const msg = describeQueueResult(result, preview.to[0]);
      if (result.mail === 'queued' || result.mail === 'duplicate') {
        toast.success(msg.title, { description: msg.description });
        onSent?.();
        onClose();
      } else {
        toast.warning(msg.title, { description: msg.description });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Onbekende fout';
      toast.error('Versturen mislukt', { description: message, duration: 10000 });
      if (/gewijzigd/.test(message)) load(documentId);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={Boolean(documentId)} onOpenChange={(open) => { if (!open && !sending) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{resend ? 'Document opnieuw versturen' : 'Controleren & versturen'}</DialogTitle>
          <DialogDescription>
            Dit is precies de e-mail en bijlage die de klant ontvangt. Er wordt pas iets verstuurd als je op versturen klikt.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-20 text-sm text-gray-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Voorbeeld laden...
          </div>
        )}

        {error && !loading && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p>{error}</p>
              {documentId && (
                <Button variant="outline" size="sm" onClick={() => load(documentId)}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Opnieuw proberen
                </Button>
              )}
            </div>
          </div>
        )}

        {preview && !loading && (
          <div className="space-y-4">
            <div className="grid gap-2 rounded-lg border bg-gray-50 p-4 text-sm sm:grid-cols-[110px_1fr]">
              <span className="text-gray-500">Onderwerp</span>
              <span className="font-medium text-gray-900">{preview.subject}</span>
              <span className="text-gray-500">Aan</span>
              <span className="text-gray-900">{preview.to.join(', ')}</span>
              {preview.bcc.length > 0 && (
                <>
                  <span className="text-gray-500">Kopie (BCC)</span>
                  <span className="text-gray-900">{preview.bcc.join(', ')}</span>
                </>
              )}
              <span className="text-gray-500">Bijlage</span>
              <span className="flex flex-wrap items-center gap-2 text-gray-900">
                {preview.attachment.name}
                {preview.paid && (
                  <Badge className="bg-[#e7e1d6] text-[#1e2b22] border-[#1e2b22]/30 hover:bg-[#e7e1d6]">
                    <CircleCheck className="w-3 h-3 mr-1" /> Betaald – zonder betaalinstructies
                  </Badge>
                )}
              </span>
            </div>
            <iframe title="PDF-bijlage" src={pdfUrl} className="w-full h-[55vh] rounded-lg border" />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onClose} disabled={sending}>Annuleren</Button>
          <Button variant="outline" onClick={() => preview && downloadPreviewPdf(preview)} disabled={!preview || sending}>
            <Download className="w-4 h-4 mr-2" /> Download deze PDF
          </Button>
          <Button onClick={handleSend} disabled={!preview || sending || loading} className="bg-jv-primary hover:bg-jv-primary/90">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {resend ? 'Opnieuw versturen naar klant' : 'Versturen naar klant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
